<template>
  <div>
    <div class="d-flex justify-end mb-4">
      <qtm-menu-select v-model="sort" item-value="value" :items="sortOptions" label="Sort" />
    </div>
    <qtm-content-block v-for="changeOrder in sortedChangeOrders" :key="changeOrder.id" class="mb-4">
      <div class="d-flex align-center justify-space-between mb-4">
        <div class="qtm-body">
          <span v-if="changeOrder.revNumber === 0">Initial Purchase Order (Purchase Order Revision 0)</span>
          <span v-else>
            <span v-if="!changeOrder.date_issued">
              <v-icon color="mid-dark-grey">mdi-clock-outline</v-icon>
              Pending
            </span>
            Change Order {{ changeOrder.revNumber }} (Purchase Order Revision {{ changeOrder.revNumber }})
          </span>
          -
          {{ dateTime(changeOrder.timestamp) }}
        </div>
        <sync-status v-if="isIntegrated" :order="order" :po="po" :revision="changeOrder" />
      </div>

      <change-order-table :change-order="changeOrder" :show-tax="anyTax" />
      <attachment-preview
        v-for="attachment in getAttachments(changeOrder.id)"
        :key="attachment.id"
        :attachment="attachment"
        class="mt-4"
        dense
      />
    </qtm-content-block>
  </div>
</template>

<script setup lang="ts">
import type { Attachment, Order, PurchaseOrder, Revision } from '@quotetome/materials-api'
import { dateTime } from '~/models/filters'
import AttachmentPreview from '@/components/attachments/attachment-preview.vue'
import ChangeOrderTable from '@/components/purchase-orders/change-order-table.vue'
import SyncStatus from '@/components/purchase-orders/sync-status.vue'

export interface Props {
  attachments?: Attachment[]
  order: Order
  po: PurchaseOrder
}

const props = withDefaults(defineProps<Props>(), {
  attachments: () => []
})

const sortOptions = [
  { text: 'Oldest to Newest', value: 'oldest' },
  { text: 'Newest to Oldest', value: 'newest' }
]

const sort = ref(sortOptions[0])

interface OrderedRevision extends Revision {
  revNumber: number
}

const changeOrdersWithInitial = computed(() => {
  const changeOrders: OrderedRevision[] = props.po.change_orders.map((co, index) => ({
    ...co,
    revNumber: index,
  }))

  return changeOrders
})

const sortedChangeOrders = computed(() => {
  const changeOrders = changeOrdersWithInitial.value

  return changeOrders.sort((a, b) => {
    if (sort.value?.value === 'oldest') {
      return (a.timestamp as number) - (b.timestamp as number)
    }
    else {
      return (b.timestamp as number) - (a.timestamp as number)
    }
  })
})

const anyTax = computed(() => props.po.change_orders.some(changeOrder => {
  return changeOrder.items.some(item => item.displayable_tax)
}))

const isIntegrated = computed(() => !!props.po.jobsite?.accounting_id)

const getAttachments = (id: number) => props.attachments.filter(attachment => attachment.object_id === id)
</script>
